//@flow

import type {
  MessageResponse,
  ConversationMessage,
  WhatsAppWebMessage,
  FacebookMessengerMessage,
  InstagramMessage,
  MessageType,
  MessageMultimedia,
  ReactionMessage,
  RepliedMessage,
  MessageMetadata,
} from '../../../../entities';
import type {WhatsAppApiMessage} from '../../../../entities/multichat/message/whatsapp-api';
import {extractFileData} from '../../../../utils/file';
import {v4 as uuidv4} from 'uuid';

import {decryptMedia} from './decrypt-whatsapp-message';

type CommonParams = {
  fromMe: boolean,
  id: string,
  messageType: MessageType,
  receivedAt: number,
  rawReceivedAt: string,
  repliedMessage: RepliedMessage,
  metadata: MessageMetadata,
};
type WhatsAppWebParams = {
  messageContent: WhatsAppWebMessage,
} & CommonParams;

function getMediaUrl(media) {
  return media.URL || media.url;
}

async function whatsappMessageToConversationMessage(
  params: WhatsAppWebParams
): Promise<ConversationMessage> {
  // $FlowFixMe
  const {
    fromMe,
    id,
    messageType,
    messageContent,
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  } = params;

  const {
    conversation,
    documentMessage,
    imageMessage,
    locationMessage,
    // stickerMessage,
    videoMessage,
    audioMessage,
  } = messageContent;

  const message: ConversationMessage = {
    id,
    fromMe,
    messageType,
    service: 'WHATSAPP_WEB',
    text: conversation,
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  };

  if (imageMessage) {
    const {mimetype, fileLength, mediaKey, caption} = imageMessage;
    const url = getMediaUrl(imageMessage);
    const blob = await decryptMedia(url, mediaKey, 'IMAGE', mimetype);

    message.images = [
      {
        mimetype,
        size: fileLength,
        src: blob,
      },
    ];

    message.text = caption;
  }

  if (videoMessage) {
    const {mimetype, fileLength, mediaKey, caption} = videoMessage;
    const url = getMediaUrl(videoMessage);
    const blob = await decryptMedia(url, mediaKey, 'VIDEO', mimetype);

    message.videos = [
      {
        mimetype,
        size: fileLength,
        src: blob,
      },
    ];

    message.text = caption;
  }

  if (audioMessage) {
    const {mimetype, fileLength, mediaKey} = audioMessage;
    const url = getMediaUrl(audioMessage);
    const blob = await decryptMedia(url, mediaKey, 'AUDIO', mimetype);

    message.audios = [
      {
        mimetype,
        size: fileLength,
        src: blob,
      },
    ];
  }

  if (documentMessage) {
    const {mimetype, title, fileLength, mediaKey, caption} = documentMessage;
    const url = getMediaUrl(documentMessage);
    const blob = await decryptMedia(url, mediaKey, 'DOCUMENT', mimetype);

    message.documents = [
      {
        mimetype,
        size: fileLength,
        src: blob,
        name: title,
      },
    ];

    message.text = caption;
  }

  if (locationMessage) {
    message.messageType = 'LOCATION';
    message.coords = {
      lat: locationMessage.degreesLatitude,
      lng: locationMessage.degreesLongitude,
    };
  }
  return message;
}

type InstagramParams = {
  messageContent: InstagramMessage,
} & CommonParams;

export async function instagramMessageToConversationMessage(
  params: InstagramParams
): Promise<ConversationMessage> {
  const {
    fromMe,
    id,
    messageType,
    messageContent,
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  } = params;

  const message: ConversationMessage = {
    fromMe,
    id,
    messageType,
    service: 'INSTAGRAM',
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  };

  if (messageType === 'TEXT') {
    return {
      ...message,
      text: messageContent.text,
    };
  }

  const {error: isUnreachable, size, type: mimetype} = await extractFileData(
    messageContent.payload.url
  );

  if (messageType === 'IMAGE') {
    return {
      ...message,
      isUnreachable,
      images: [
        {
          src: messageContent.payload.url,
          size,
          mimetype,
        },
      ],
    };
  }

  if (messageType === 'VIDEO') {
    return {
      ...message,
      isUnreachable,
      videos: [
        {
          size,
          mimetype,
          src: messageContent.payload.url,
        },
      ],
    };
  }

  if (messageType === 'DOCUMENT') {
    return {
      ...message,
      isUnreachable,
      documents: [
        {
          size,
          mimetype,
          src: messageContent.payload.url,
        },
      ],
    };
  }

  if (messageType === 'AUDIO') {
    return {
      ...message,
      isUnreachable,
      audios: [
        {
          size,
          mimetype,
          src: messageContent.payload.url,
        },
      ],
    };
  }

  return message;
}

type FacebookParams = {
  messageContent: FacebookMessengerMessage,
} & CommonParams;

export async function facebookMessageToConversationMessage(
  params: FacebookParams
): Promise<ConversationMessage> {
  const {
    fromMe,
    id,
    messageType,
    messageContent,
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  } = params;

  const message: ConversationMessage = {
    fromMe,
    rawReceivedAt,
    id,
    messageType,
    service: 'FB_MESSENGER',
    receivedAt,
    repliedMessage,
    metadata,
  };

  if (messageType === 'TEXT') {
    return {
      ...message,
      text: messageContent.text,
    };
  }

  if (messageType === 'IMAGE') {
    return {
      ...message,
      images: [
        {
          src: messageContent.payload.url,
        },
      ],
    };
  }

  const {error: isUnreachable, size, type: mimetype} = await extractFileData(
    messageContent.payload.url
  );

  const messageMultimedia: MessageMultimedia = {
    src: messageContent.payload.url,
    size,
    mimetype,
  };

  if (messageType === 'VIDEO') {
    return {
      ...message,
      isUnreachable,
      videos: [messageMultimedia],
    };
  }

  if (messageType === 'DOCUMENT') {
    return {
      ...message,
      isUnreachable,
      documents: [messageMultimedia],
    };
  }

  if (messageType === 'AUDIO') {
    return {
      ...message,
      isUnreachable,
      audios: [messageMultimedia],
    };
  }

  return message;
}

function renderTextWhatsAppTemplate(
  components: Array<{
    type: string,
    text?: string,
    buttons?: Array<{text: string, type: string}>,
  }>,
  templateParameters: {
    components?: string[] | null,
  }
): string {
  const renderedComponents: string[] = [];

  const parsedParameters = templateParameters?.components
    ? templateParameters.components.map(component => {
        return JSON.parse(atob(component));
      })
    : [];

  components.forEach(component => {
    const parameters = parsedParameters.find(
      paramComponent =>
        paramComponent.type.toLowerCase() === component.type.toLowerCase()
    );

    if (component.text && parameters) {
      let renderedText = component.text;
      parameters.parameters.forEach((param, index) => {
        const placeholder = `{{${index + 1}}}`;
        renderedText = renderedText.replace(placeholder, param.text);
      });
      renderedComponents.push(renderedText);
    } else if (component.text) {
      renderedComponents.push(component.text);
    }

    /*     if (component.buttons) {
      const buttonsText = component.buttons
        .map(button => `- ${button.text}`)
        .join('\n');
      renderedComponents.push(buttonsText);
    } */
  });

  return renderedComponents.join('\n\n');
}

type TemplateMedia = {
  images: string[],
  videos: string[],
};

function renderMediaWhatsAppTemplate(
  components: Array<{
    type: string,
    text?: string,
    format?: string,
    buttons?: Array<{text: string, type: string}>,
  }>,
  templateParameters: {
    components?: string[] | null,
  }
): TemplateMedia {
  const parsedParameters = templateParameters?.components
    ? templateParameters.components.map(component => {
        return JSON.parse(atob(component));
      })
    : [];

  const images: string[] = [];
  const videos: string[] = [];

  components.forEach(component => {
    const parameters = parsedParameters.find(
      paramComponent =>
        paramComponent.type.toLowerCase() === component.type.toLowerCase()
    );

    if (component.format === 'IMAGE' && parameters) {
      const url = parameters.parameters[0]?.image?.link;
      if (url) images.push(url);
    }

    if (component.format === 'VIDEO' && parameters) {
      const url = parameters.parameters[0]?.video?.link;
      if (url) videos.push(url);
    }
  });

  return {
    images: images.filter(Boolean),
    videos: videos.filter(Boolean),
  };
}

type WhatsAppApiParams = {
  messageContent: WhatsAppApiMessage,
} & CommonParams;

export async function whatsappApiMessageToConversationMessage(
  params: WhatsAppApiParams
): Promise<ConversationMessage> {
  const {
    fromMe,
    id,
    messageType,
    messageContent,
    receivedAt,
    rawReceivedAt,
    repliedMessage,
    metadata,
  } = params;

  const {text, template, templateParameters} = messageContent;

  const commonMessage: ConversationMessage = {
    id,
    fromMe,
    messageType,
    receivedAt,
    rawReceivedAt,
    service: 'WHATSAPP_API',
    repliedMessage,
    metadata,
  };

  if (messageType === 'TEXT') {
    return {
      ...commonMessage,
      text: text.body,
    };
  }

  if (messageType === 'TEMPLATE' && template && templateParameters) {
    const text = renderTextWhatsAppTemplate(
      template.components,
      templateParameters
    );

    const media = renderMediaWhatsAppTemplate(
      template.components,
      templateParameters
    );
    return {
      ...commonMessage,
      text: text,
      messageType: 'TEXT',
      images:
        media.images.length > 0 ? media.images.map(src => ({src})) : undefined,
      videos:
        media.videos.length > 0 ? media.videos.map(src => ({src})) : undefined,
    };
  }

  if (messageType === 'IMAGE' && messageContent.image) {
    return {
      ...commonMessage,
      images: [
        {
          mediaId: messageContent.image?.id,
          mimetype: messageContent?.image?.mime_type,
        },
      ],
    };
  }

  if (messageType === 'VIDEO' && messageContent.video) {
    return {
      ...commonMessage,
      videos: [
        {
          mediaId: messageContent.video?.id,
          mimetype: messageContent?.video?.mime_type,
        },
      ],
    };
  }

  if (messageType === 'AUDIO' && messageContent.audio) {
    return {
      ...commonMessage,
      audios: [
        {
          mediaId: messageContent.audio?.id,
          mimetype: messageContent?.audio?.mime_type,
        },
      ],
    };
  }

  if (messageType === 'DOCUMENT' && messageContent.document) {
    return {
      ...commonMessage,
      documents: [
        {
          mediaId: messageContent.document?.id,
          mimetype: messageContent?.document?.mime_type,
        },
      ],
    };
  }

  return commonMessage;
}

export async function conversationMessageAdapter(
  messageResponse: MessageResponse
): Promise<ConversationMessage | null> {
  const {
    service,
    fromMe,
    providerMessageId,
    messageType,
    receivedAt,
    rawReceivedAt,
    metadata,
  } = messageResponse;

  let repliedMessage: RepliedMessage | null = null;
  if (
    messageResponse.repliedMessage &&
    messageResponse.repliedMessage.messageId &&
    messageResponse.repliedMessage.messageReceivedAt
  ) {
    repliedMessage = messageResponse.repliedMessage;
  }

  try {
    if (messageResponse.service === 'WHATSAPP_WEB') {
      const messageContent = messageResponse.message;
      const resolvedId = providerMessageId || uuidv4();

      const result = await whatsappMessageToConversationMessage({
        id: resolvedId,
        fromMe,
        messageType,
        messageContent,
        receivedAt,
        rawReceivedAt,
        repliedMessage,
        metadata,
      });
      return result;
    }

    if (messageResponse.service === 'INSTAGRAM') {
      const result = await instagramMessageToConversationMessage({
        id: providerMessageId,
        fromMe,
        messageType,
        messageContent: messageResponse.message,
        receivedAt,
        rawReceivedAt,
        repliedMessage,
        metadata,
      });

      return result;
    }

    if (service === 'FB_MESSENGER') {
      const result = await facebookMessageToConversationMessage({
        id: providerMessageId,
        fromMe,
        messageType,
        messageContent: messageResponse.message,
        receivedAt,
        rawReceivedAt,
        repliedMessage,
        metadata,
      });

      return result;
    }

    if (messageResponse.service === 'WHATSAPP_API') {
      const resolvedId = providerMessageId || uuidv4();
      const messageContent = messageResponse.message;

      return whatsappApiMessageToConversationMessage({
        id: resolvedId,
        fromMe,
        messageType,
        messageContent,
        receivedAt,
        rawReceivedAt,
        repliedMessage,
        metadata,
      });
    }

    throw new Error('Service not supported');
  } catch (error) {
    console.error('Error adapting message', error);
    return null;
  }
}

export function mapRawToReaction(raw: MessageResponse): ReactionMessage | null {
  if (raw.messageType !== 'REACTION') return null;

  switch (raw.service) {
    case 'WHATSAPP_WEB': {
      const {
        repliedMessage,
        message: {reactionMessage},
        fromMe,
      } = raw;

      if (!repliedMessage || !reactionMessage) {
        return null;
      }

      return {
        id: uuidv4(),
        reaction: reactionMessage?.text || '',
        messageId: repliedMessage.messageId,
        fromMe,
      };
    }

    default:
      return null;
  }
}
